/**
 * Compact "what happens next" chip.
 *
 * The next event is the earliest open task on a lead or customer. It is shown
 * identically on the lead board, the customer list, My Day and the customer
 * header so a dealer learns one visual and reads it everywhere.
 *
 * A missing next event is not blank — it is a warning. Every open lead is
 * meant to have a next step, and the absence is the thing a manager needs
 * to see.
 */

import { relativeDue, formatDateTime } from '../lib/datetime';

export default function NextEventChip({
  nextEvent,
  onSet,
  compact = false,
  showOwner = false,
}) {
  if (!nextEvent) {
    return (
      <span className="flex-row" style={{ alignItems: 'center', gap: 6 }}>
        <span className="pill warning" title="Every open lead should have a next step">
          No next step
        </span>
        {onSet && (
          <button type="button" className="btn small" onClick={onSet}>+ Set</button>
        )}
      </span>
    );
  }

  const rel = relativeDue(nextEvent.dueAt);
  const overdue = nextEvent.overdue ?? rel.overdue;

  return (
    <span
      className="flex-row"
      style={{ alignItems: 'center', gap: 8, minWidth: 0 }}
      title={`${nextEvent.title} · ${formatDateTime(nextEvent.dueAt)}${
        nextEvent.assigneeName ? ` · ${nextEvent.assigneeName}` : ''
      }`}
    >
      <span className={`pill${overdue ? ' danger filled' : ''}`}>
        {nextEvent.reasonLabel || nextEvent.type || 'Task'}
      </span>

      {!compact && (
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--text-secondary)',
          }}
        >
          {nextEvent.title}
        </span>
      )}

      <span
        className="mono text-xs"
        style={{
          color: overdue ? 'var(--danger)' : 'var(--text-muted)',
          fontWeight: overdue ? 600 : 400,
          whiteSpace: 'nowrap',
        }}
      >
        {rel.label}
      </span>

      {showOwner && nextEvent.assigneeName && (
        <span className="mono text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
          · {nextEvent.assigneeName}
        </span>
      )}
    </span>
  );
}
